import { getDeviceType } from '@/utils/trackingSpine'
import {
  MISSION_ATTEMPT_END_REASON_PRIORITY,
  MISSION_ATTEMPT_END_REASONS,
  MISSION_ATTEMPT_EXIT_REASON_KEY,
  MISSION_ATTEMPT_PENDING_END_KEY,
  MISSION_ATTEMPT_SESSION_KEY
} from '@/constants/missionAttemptEndReasons'

export function isOnline() {
  if (typeof navigator === 'undefined') return null
  return navigator.onLine
}

export function getConnectionType() {
  if (typeof navigator === 'undefined') return null
  return navigator.connection?.effectiveType || null
}

export function getViewport() {
  if (typeof window === 'undefined') return null
  const width = window.innerWidth || 0
  const height = window.innerHeight || 0
  if (!width || !height) return null
  return `${width}x${height}`
}

export function getMissionAttemptClientContext() {
  return {
    deviceType: getDeviceType() || null,
    online: isOnline(),
    connectionType: getConnectionType(),
    viewport: getViewport()
  }
}

export function buildAttemptStartData() {
  const context = getMissionAttemptClientContext()
  return {
    startTime: new Date(),
    endTime: null,
    duration: 0,
    endReason: MISSION_ATTEMPT_END_REASONS.IN_PROGRESS,
    deviceType: context.deviceType,
    onlineAtStart: context.online,
    onlineAtEnd: null,
    connectionDropCount: 0,
    connectionType: context.connectionType,
    viewport: context.viewport
  }
}

export function getNavigationType() {
  if (typeof performance === 'undefined') return null
  const entry = performance.getEntriesByType?.('navigation')?.[0]
  return entry?.type || null
}

export function getAttemptStartMs(startTime) {
  if (!startTime) return null
  if (startTime instanceof Date) {
    const value = startTime.getTime()
    return Number.isNaN(value) ? null : value
  }
  if (typeof startTime.toDate === 'function') {
    const value = startTime.toDate().getTime()
    return Number.isNaN(value) ? null : value
  }
  if (typeof startTime.seconds === 'number') {
    return startTime.seconds * 1000
  }
  const parsed = new Date(startTime).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

export function findAttemptIndex(attempts, startTime) {
  const target = getAttemptStartMs(startTime)
  if (target == null || !Array.isArray(attempts)) return -1
  return attempts.findIndex((attempt) => {
    const value = getAttemptStartMs(attempt?.startTime)
    return value != null && Math.abs(value - target) < 1000
  })
}

export function shouldReplaceEndReason(current, next) {
  if (!next) return false
  if (!current) return true
  const currentRank = MISSION_ATTEMPT_END_REASON_PRIORITY[current] ?? 0
  const nextRank = MISSION_ATTEMPT_END_REASON_PRIORITY[next] ?? 0
  return nextRank >= currentRank
}

export function isUnfinishedAttempt(attempt) {
  if (!attempt) return false
  const reason = attempt.endReason
  if (
    reason === MISSION_ATTEMPT_END_REASONS.IN_PROGRESS ||
    reason === MISSION_ATTEMPT_END_REASONS.PAGE_UNLOAD
  ) {
    return true
  }
  return !attempt.endTime
}

function toDateOrNull(value) {
  const ms = getAttemptStartMs(value)
  return ms == null ? null : new Date(ms)
}

export function applyAttemptEnd(attempt, startTime, endMeta = {}) {
  const startMs =
    getAttemptStartMs(startTime) || getAttemptStartMs(attempt?.startTime)
  const existingEnd = toDateOrNull(attempt?.endTime)
  let endTime

  if (endMeta.endTime instanceof Date) {
    endTime = endMeta.endTime
  } else if (endMeta.preserveDuration) {
    endTime =
      existingEnd ||
      (startMs != null
        ? new Date(startMs + (attempt?.duration || 0))
        : new Date())
  } else {
    endTime = new Date()
  }

  const duration =
    startMs != null
      ? Math.max(0, endTime.getTime() - startMs)
      : attempt?.duration || 0
  const nextReason = endMeta.endReason
  const endReason = shouldReplaceEndReason(attempt?.endReason, nextReason)
    ? nextReason
    : attempt?.endReason || nextReason || MISSION_ATTEMPT_END_REASONS.UNKNOWN_END

  return {
    ...attempt,
    endTime,
    duration,
    endReason,
    onlineAtEnd:
      endMeta.onlineAtEnd !== undefined
        ? endMeta.onlineAtEnd
        : (attempt?.onlineAtEnd ?? null),
    connectionDropCount:
      endMeta.connectionDropCount !== undefined
        ? endMeta.connectionDropCount
        : (attempt?.connectionDropCount ?? 0)
  }
}

export function sumAttemptDurations(attempts) {
  if (!Array.isArray(attempts)) return 0
  return attempts.reduce((total, attempt) => total + (attempt?.duration || 0), 0)
}

export function applyEndToAttempts(attempts, startTime, endMeta = {}) {
  const list = Array.isArray(attempts) ? [...attempts] : []
  const index = findAttemptIndex(list, startTime)
  if (index === -1) {
    return {
      attempts: list,
      changed: false,
      duration: 0,
      totalTimeSpent: sumAttemptDurations(list)
    }
  }
  list[index] = applyAttemptEnd(list[index], startTime, endMeta)
  return {
    attempts: list,
    changed: true,
    duration: list[index].duration || 0,
    totalTimeSpent: sumAttemptDurations(list)
  }
}

export function resolveUnfinishedEndReason(attempt) {
  if (attempt?.endReason === MISSION_ATTEMPT_END_REASONS.PAGE_UNLOAD) {
    return MISSION_ATTEMPT_END_REASONS.TAB_OR_WINDOW_CLOSE
  }
  if (attempt?.onlineAtStart === false || attempt?.onlineAtEnd === false) {
    return MISSION_ATTEMPT_END_REASONS.CONNECTION_LOST
  }
  return MISSION_ATTEMPT_END_REASONS.UNKNOWN_END
}

export function applyEndToUnfinishedAttempts(attempts, endMeta = {}) {
  const list = Array.isArray(attempts) ? [...attempts] : []
  const exceptMs = getAttemptStartMs(endMeta.exceptStartTime)
  let changed = false
  let duration = 0
  const next = list.map((attempt) => {
    if (!isUnfinishedAttempt(attempt)) return attempt
    const attemptMs = getAttemptStartMs(attempt.startTime)
    if (
      exceptMs != null &&
      attemptMs != null &&
      Math.abs(attemptMs - exceptMs) < 1000
    ) {
      return attempt
    }
    const reason = endMeta.resolveReason
      ? endMeta.resolveReason(attempt)
      : endMeta.endReason || resolveUnfinishedEndReason(attempt)
    changed = true
    const updated = applyAttemptEnd(attempt, attempt.startTime, {
      ...endMeta,
      endReason: reason,
      preserveDuration: endMeta.preserveDuration !== false
    })
    duration = updated.duration || 0
    return updated
  })
  return {
    attempts: next,
    changed,
    duration,
    totalTimeSpent: sumAttemptDurations(next)
  }
}

export function mapAttemptForAnalytics(attempt) {
  return {
    startTime: toDateOrNull(attempt?.startTime),
    endTime: toDateOrNull(attempt?.endTime),
    duration: attempt?.duration || 0,
    endReason: attempt?.endReason || null,
    deviceType: attempt?.deviceType || null,
    onlineAtStart: attempt?.onlineAtStart ?? null,
    onlineAtEnd: attempt?.onlineAtEnd ?? null,
    connectionDropCount: attempt?.connectionDropCount ?? 0,
    connectionType: attempt?.connectionType || null,
    viewport: attempt?.viewport || null
  }
}

function readJson(storage, key) {
  if (!storage) return null
  try {
    const raw = storage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeJson(storage, key, value) {
  if (!storage) return
  try {
    storage.setItem(key, JSON.stringify(value))
  } catch {
    void 0
  }
}

function removeKey(storage, key) {
  if (!storage) return
  try {
    storage.removeItem(key)
  } catch {
    void 0
  }
}

export function markMissionAttemptExitReason(reason) {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(MISSION_ATTEMPT_EXIT_REASON_KEY, reason)
  } catch {
    void 0
  }
}

export function consumeMissionAttemptExitReason() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const reason = sessionStorage.getItem(MISSION_ATTEMPT_EXIT_REASON_KEY)
    if (reason) sessionStorage.removeItem(MISSION_ATTEMPT_EXIT_REASON_KEY)
    return reason
  } catch {
    return null
  }
}

export function writeOpenMissionAttemptSession(payload) {
  writeJson(
    typeof sessionStorage === 'undefined' ? null : sessionStorage,
    MISSION_ATTEMPT_SESSION_KEY,
    payload
  )
}

export function readOpenMissionAttemptSession() {
  return readJson(
    typeof sessionStorage === 'undefined' ? null : sessionStorage,
    MISSION_ATTEMPT_SESSION_KEY
  )
}

export function patchOpenMissionAttemptSession(partial) {
  const current = readOpenMissionAttemptSession()
  if (!current) return
  writeOpenMissionAttemptSession({ ...current, ...partial })
}

export function clearOpenMissionAttemptSession() {
  removeKey(
    typeof sessionStorage === 'undefined' ? null : sessionStorage,
    MISSION_ATTEMPT_SESSION_KEY
  )
}

export function writePendingMissionAttemptEnd(payload) {
  writeJson(
    typeof sessionStorage === 'undefined' ? null : sessionStorage,
    MISSION_ATTEMPT_PENDING_END_KEY,
    payload
  )
  writeJson(
    typeof localStorage === 'undefined' ? null : localStorage,
    MISSION_ATTEMPT_PENDING_END_KEY,
    payload
  )
}

export function readPendingMissionAttemptEnd() {
  const fromSession = readJson(
    typeof sessionStorage === 'undefined' ? null : sessionStorage,
    MISSION_ATTEMPT_PENDING_END_KEY
  )
  if (fromSession) return fromSession
  return readJson(
    typeof localStorage === 'undefined' ? null : localStorage,
    MISSION_ATTEMPT_PENDING_END_KEY
  )
}

export function clearPendingMissionAttemptEnd() {
  removeKey(
    typeof sessionStorage === 'undefined' ? null : sessionStorage,
    MISSION_ATTEMPT_PENDING_END_KEY
  )
  removeKey(
    typeof localStorage === 'undefined' ? null : localStorage,
    MISSION_ATTEMPT_PENDING_END_KEY
  )
}

export function buildAttemptEndMeta(endReason, extra = {}) {
  return {
    endReason,
    onlineAtEnd: extra.onlineAtEnd ?? isOnline(),
    connectionDropCount: extra.connectionDropCount ?? 0
  }
}

export function isLikelyNetworkError(error) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  const code = error?.code || ''
  const message = String(error?.message || '').toLowerCase()
  if (code === 'unavailable' || code === 'deadline-exceeded') return true
  return (
    message.includes('network') ||
    message.includes('offline') ||
    message.includes('failed to fetch')
  )
}

export function resolveUnloadEndReason({ markedReason, onlineNow } = {}) {
  if (markedReason) return markedReason
  if (onlineNow === false) return MISSION_ATTEMPT_END_REASONS.CONNECTION_LOST
  return MISSION_ATTEMPT_END_REASONS.PAGE_UNLOAD
}

export async function reconcileMissionAttemptOnLoad({
  missionId,
  resolved,
  endAttempt,
  finalizeUnfinished
}) {
  const pending = readPendingMissionAttemptEnd()
  if (pending?.startTime) {
    await endAttempt({
      missionId: pending.missionId || missionId,
      startTime: new Date(pending.startTime),
      childId: pending.childId || resolved?.childId,
      userType: pending.userType || resolved?.userType,
      endMeta: pending.endMeta || {}
    })
    clearPendingMissionAttemptEnd()
  }

  const open = readOpenMissionAttemptSession()
  const navType = getNavigationType()
  const isReload =
    Boolean(open?.startTime) &&
    String(open.missionId) === String(missionId) &&
    navType === 'reload'

  if (isReload) {
    await endAttempt({
      missionId,
      startTime: new Date(open.startTime),
      childId: open.childId || resolved?.childId,
      userType: open.userType || resolved?.userType,
      endMeta: buildAttemptEndMeta(MISSION_ATTEMPT_END_REASONS.BROWSER_REFRESH, {
        connectionDropCount: open.connectionDropCount || 0
      })
    })
    clearOpenMissionAttemptSession()
  }

  await finalizeUnfinished({
    preserveDuration: true,
    onlineAtEnd: isOnline(),
    resolveReason: resolveUnfinishedEndReason,
    exceptStartTime: isReload ? new Date(open.startTime) : null
  })
}
